import { FC, ReactNode } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { TPrePurchase, TPurchase } from "@/api";
import { country } from "@/config/country";

const formatMoney = (n: number) =>
  new Intl.NumberFormat(country.locale ?? "es-CO", {
    style: "currency",
    currency: country.currency ?? "COP",
    maximumFractionDigits: 0,
  }).format(n || 0);

const Row: FC<{
  label: ReactNode;
  value: number;
  negative?: boolean;
  bold?: boolean;
  accent?: "default" | "tax" | "total";
}> = ({ label, value, negative, bold, accent = "default" }) => {
  if (!value) return null;
  const color =
    accent === "tax"
      ? "success.main"
      : accent === "total"
      ? "text.primary"
      : "text.primary";
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.25 }}
    >
      <Typography
        variant="body2"
        color={accent === "total" ? "text.primary" : "text.secondary"}
        sx={{ fontWeight: bold ? 700 : 400 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: bold ? 700 : 500, color }}
      >
        {negative ? "−" : ""}
        {formatMoney(Math.abs(value))}
      </Typography>
    </Stack>
  );
};

/**
 * PricingV2 — replica del Pricing de suppliers-web. Sin la línea
 * "Subtotal con descuentos" que no aplica en CO.
 *
 * Orden de filas (cada una solo aparece si el valor tiene sentido):
 *  - Subtotal bruto (solo si subTotalBase !== subtotal)
 *  - Descuento por oferta (negativo)
 *  - Subtotal sin impuestos (cuando hay descuento por oferta)
 *  - IVA / Tax (verde)
 *  - Retención producto (negativo)
 *  - Subtotal (cuando subTotalWoTaxes !== subtotal)
 *  - Retenciones (CO, listadas por nombre)
 *  - Envío
 *  - Total
 */
export const PricingV2: FC<{ purchase: TPrePurchase | TPurchase }> = ({
  purchase,
}) => {
  const p: any = purchase ?? {};
  const subTotalBase = p.subTotalBase ?? 0;
  const subTotal = p.subtotal ?? 0;
  const subTotalWoTaxes = p.subTotalWoTaxes ?? 0;
  const discountByOffer = p.discountByOffer ?? 0;
  const totalTaxes = p.totalTaxes ?? 0;
  const productRetention = p.productRetention ?? 0;
  const shipping = p.shipping ?? 0;
  const pendingToPay = p.pendingToPay ?? p.total ?? 0;
  const retentions: { id: string; name: string; value: number }[] =
    p.retentions ?? [];
  const isCO = country.code === "co";

  return (
    <Stack divider={<Divider flexItem />} spacing={0.25}>
      <Stack spacing={0.25}>
        {subTotalBase !== subTotal && subTotalBase > 0 && (
          <Row label="Subtotal bruto" value={subTotalBase} />
        )}
        <Row label="Descuento por oferta" value={discountByOffer} negative />
        {discountByOffer !== 0 && subTotalWoTaxes > 0 && (
          <Row label="Subtotal" value={subTotalWoTaxes} />
        )}
        {totalTaxes > 0 && (
          <Row label="IVA" value={totalTaxes} accent="tax" bold />
        )}
        <Row label="Retención producto" value={productRetention} negative />
        {subTotalWoTaxes !== subTotal && subTotal > 0 && (
          <Row label="Subtotal" value={subTotal} />
        )}
        {isCO &&
          retentions.map((r) => (
            <Row
              key={r.id}
              label={r.name}
              value={r.value}
              negative
            />
          ))}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ py: 0.25 }}
        >
          <Typography variant="body2" color="text.secondary">
            Envío
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatMoney(shipping)}
          </Typography>
        </Stack>
      </Stack>

      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ pt: 1 }}
        >
          <Typography sx={{ fontWeight: 700 }}>Total</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem" }}>
            {formatMoney(pendingToPay)}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
};
